import React, { useEffect, useState } from 'react';
import { elements, inputs, feedback } from '@ecosystem/newron-design-system';
import { Market, PeriodAggregation, TimeFrame, TimeFrameOption } from '../types';
import './periodSelector.css';
import { MonthsSelector } from './monthsSelector';
import { WeeksSelector } from './weeksSelector';
import { isCustomMonthly, isCustomWeekly } from './periodSelector.utils';
import { getPeriodicity } from './weeksSelector.utils';
import { currentProposition } from '../../utils/currentProposition';
import { MONTH_OPTIONS } from './util';

const { Icon } = elements;
const { Checkbox, CheckboxState, RadioButtons, Select } = inputs;
const { Tooltip, DotsLoader } = feedback;

export const DISMISSED_KEY = () => `incomplete_period_notice_dismissed_${currentProposition()}`;

type Props = {
  options: TimeFrameOption[];
  markets: Market[];
  aggregation: PeriodAggregation;
  currentValue: TimeFrame;
  isDistributionPanel?: boolean;
  ytdStartMonth?: number;
  isYtdLoading?: boolean;
  onSelect: (value: TimeFrameOption, removeAffectedMarkets?: boolean) => void;
  onAggregationChange: (aggregation: PeriodAggregation) => void;
  handlePartialTimeFrameData?: (option: TimeFrameOption, removeAffectedMarkets: boolean) => void;
  onYtdStartMonthChange?: (month: number) => void;
};

function PeriodSelector({
  options,
  markets,
  aggregation,
  currentValue,
  isDistributionPanel,
  ytdStartMonth = 1,
  isYtdLoading = false,
  onSelect,
  onAggregationChange,
  handlePartialTimeFrameData,
  onYtdStartMonthChange,
}: Readonly<Props>) {
  const periodicity = getPeriodicity(aggregation);

  const defaultAggregation = isDistributionPanel ? PeriodAggregation.Week : PeriodAggregation.Month;
  const [showIncompleteMsg, setShowIncompleteMsg] = useState<boolean>(false);
  const [customAggregation, setCustomAggregation] = useState<PeriodAggregation.Month | PeriodAggregation.Week>(
    defaultAggregation
  );

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISSED_KEY());
    setShowIncompleteMsg(dismissed !== 'true');
  }, []);

  useEffect(() => {
    if (isCustomWeekly(currentValue)) {
      setCustomAggregation(PeriodAggregation.Week);
    } else if (isCustomMonthly(currentValue)) {
      setCustomAggregation(PeriodAggregation.Month);
    }
  }, [currentValue]);

  const notOnAll = options.filter(opt => opt.aggregation === aggregation && !opt.availableOnAll);
  const onAll = options.filter(opt => opt.aggregation === aggregation && opt.availableOnAll);
  const hasWeekly = options.some(opt => opt.aggregation === PeriodAggregation.Week);

  const handleItemClick = (opt: TimeFrameOption) => {
    onSelect(opt);
  };

  const handleDismiss = () => {
    setShowIncompleteMsg(false);
    localStorage.setItem(DISMISSED_KEY(), 'true');
  };

  const renderYTDMonthSelector = () => {
    const handleMonthChange = (value: string) => {
      if (isYtdLoading) return;

      const month = Number(value);
      onYtdStartMonthChange(month);
    };

    return (
      <div className="mt-2">
        <Select
          name="ytd-start-month"
          value={String(ytdStartMonth)}
          options={MONTH_OPTIONS}
          onChange={handleMonthChange}
          disabled={isYtdLoading}
          placeholder="Select start month"
        />
      </div>
    );
  };

  return (
    <div
      className={`flex flex-column overflow-visible flex-grow ${
        PeriodAggregation.Custom === aggregation ? 'overflow-visible' : 'overflow-y-auto'
      } overscroll-contain pb-2 pt-2`}
    >
      {[PeriodAggregation.Quarter, PeriodAggregation.RollingQuarter].includes(aggregation) && (
        <div className="border-b border-light mx-3.5 pt-2 pb-4 mb-2">
          <Checkbox
            label="Rolling quarters"
            state={aggregation === PeriodAggregation.RollingQuarter ? CheckboxState.CHECKED : CheckboxState.UNCHECKED}
            onChange={state =>
              onAggregationChange(
                state === CheckboxState.CHECKED ? PeriodAggregation.RollingQuarter : PeriodAggregation.Quarter
              )
            }
          />
        </div>
      )}
      {[PeriodAggregation.Year, PeriodAggregation.MAT, PeriodAggregation.YTD].includes(aggregation) && (
        <div className="border-b border-light mx-3.5 pt-2 pb-4 mb-2">
          <RadioButtons
            options={[
              {
                value: PeriodAggregation.Year,
                label: 'Whole year',
              },
              {
                value: PeriodAggregation.MAT,
                label: 'Moving annual total',
              },
              {
                value: PeriodAggregation.YTD,
                label: 'Year to date',
              },
            ]}
            value={aggregation}
            onChange={val => onAggregationChange(val as PeriodAggregation)}
            isDisabled={isYtdLoading}
          />
          {aggregation === PeriodAggregation.YTD && renderYTDMonthSelector()}
        </div>
      )}
      {PeriodAggregation.Custom === aggregation && (
        <>
          {hasWeekly && (
            <div className="border-b border-light mx-3.5 pt-2 pb-4 mb-2">
              <Checkbox
                label="Weekly"
                disabled={isDistributionPanel}
                state={customAggregation === PeriodAggregation.Week ? CheckboxState.CHECKED : CheckboxState.UNCHECKED}
                onChange={state =>
                  setCustomAggregation(
                    state === CheckboxState.CHECKED ? PeriodAggregation.Week : PeriodAggregation.Month
                  )
                }
              />
            </div>
          )}
          {customAggregation === PeriodAggregation.Week && (
            <WeeksSelector
              currentValue={currentValue}
              onSelect={handlePartialTimeFrameData}
              weeklyOptions={options.filter(opt => opt.aggregation === PeriodAggregation.Week)}
              markets={markets}
            />
          )}
          {customAggregation === PeriodAggregation.Month && (
            <MonthsSelector
              currentValue={currentValue}
              onSelect={handlePartialTimeFrameData}
              monthlyOptions={options.filter(opt => opt.aggregation === PeriodAggregation.Month)}
              markets={markets}
            />
          )}
        </>
      )}
      {aggregation !== PeriodAggregation.Custom && (
        <div className="relative h-full">
          {isYtdLoading && aggregation === PeriodAggregation.YTD ? (
            <div className="h-full flex flex-col justify-center items-center py-12">
              <DotsLoader stretch />
            </div>
          ) : (
            <>
              {notOnAll.length > 0 && showIncompleteMsg && (
                <div className="text-sm px-4 py-3 bg-brand-lightest mb-2">
                  <div className="flex flex-row gap-2">
                    <Icon color="lighter">
                      <Icon.icons.Fractional />
                    </Icon>
                    <p className="flex-grow text sm">
                      This mark means data is not available for all selected markets yet.
                    </p>
                  </div>
                  <button className="block text-brand py-2" onClick={handleDismiss}>
                    Dismiss
                  </button>
                </div>
              )}

              {notOnAll.map(opt => (
                <PeriodItem
                  key={opt.value}
                  option={opt}
                  isActive={opt.value === currentValue.value}
                  onClick={() => handleItemClick(opt)}
                  periodicity={periodicity}
                  selectedMarkets={markets}
                  handlePartialTimeFrameData={handlePartialTimeFrameData}
                />
              ))}

              {notOnAll.length > 0 && <div className="border-b border-light my-2 mx-2 h-px" />}

              {onAll.map((opt, index) => (
                <div key={`${index * 2}-${opt.value}`}>
                  <PeriodItem
                    key={opt.value}
                    option={opt}
                    isActive={opt.value === currentValue.value}
                    onClick={() => handleItemClick(opt)}
                    periodicity={periodicity}
                    selectedMarkets={markets}
                  />
                  {aggregation === PeriodAggregation.Quarter && opt.label.startsWith('Q1') && (
                    <div className="border-b border-light my-2 mx-2 h-px" />
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

type ItemProps = {
  option: TimeFrameOption;
  isActive: boolean;
  onClick: () => void;
  selectedMarkets: Market[];
  periodicity: string;
  handlePartialTimeFrameData?: (option: TimeFrameOption, removeAffectedMarkets: boolean) => void;
};

function PeriodItem({
  option,
  isActive,
  onClick,
  selectedMarkets,
  periodicity,
  handlePartialTimeFrameData,
}: ItemProps) {
  const { label, availableOnAll, endSequence } = option;
  const affectedMarkets = selectedMarkets.filter(m =>
    m.periods.some(p => p.periodicity === periodicity && p.sequence < endSequence)
  );

  const unavailableMarkets = selectedMarkets.filter(m => !m.periods.some(p => p.periodicity === periodicity));

  const handleClick = () => {
    if (isActive) {
      return;
    }
    if (unavailableMarkets.length > 0 || affectedMarkets.length > 0) {
      handlePartialTimeFrameData(option, false);
      return;
    }
    onClick();
  };

  const partialDataMessage = () => (
    <Tooltip placement="right">
      <Icon size="sm" color="lighter">
        <Icon.icons.Fractional />
      </Icon>
      <Tooltip.Content>
        <div className="flex flex-column gap-3" style={{ maxHeight: '12rem', overflowY: 'auto' }}>
          {affectedMarkets.length > 0 && (
            <div>
              Data is not available for:
              <ul className="ml-5" style={{ listStyleType: 'disc' }}>
                {affectedMarkets.slice(0, 4).map(({ label }, index) => (
                  <li key={`${index * 2}-${label}`}>{label}</li>
                ))}
                {affectedMarkets.slice(4).length > 0 && <span>{`and ${affectedMarkets.slice(4).length} more`}</span>}
              </ul>
            </div>
          )}
          {unavailableMarkets.length > 0 && (
            <div>
              Data is available for the below markets, but currently outside of your subscription:
              <ul className="ml-5" style={{ listStyleType: 'disc' }}>
                {unavailableMarkets.slice(0, 4).map(({ label }, index) => (
                  <li key={`${index * 2}-${label}`}>{label}</li>
                ))}
                {unavailableMarkets.slice(4).length > 0 && (
                  <span>{`and ${unavailableMarkets.slice(4).length} more`}</span>
                )}
              </ul>
            </div>
          )}
        </div>
      </Tooltip.Content>
    </Tooltip>
  );

  return (
    <button
      className={`text-left w-full p-2 flex flex-row items-center gap-2 period-row ${
        !isActive ? 'transition-colors hover:bg-brand-lightest' : 'cursor-default'
      }`}
      onClick={handleClick}
    >
      <span className={isActive ? 'visible' : 'hidden'}>
        <Icon>
          <Icon.icons.Check />
        </Icon>
      </span>
      <span className="flex-grow nowrap">{label}</span>
      {!availableOnAll && partialDataMessage()}
    </button>
  );
}

export { PeriodSelector };
